import React, { useEffect, useState, useRef, useContext } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { BookCopy, Delete, List, Paperclip, Plus, Save, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import FormSetup from './FormSetup';
import EstContext from '../../../Context/EstContext';
import Moment from 'react-moment';
import { MainWrap } from '../../../utilities/Shared';


const SetLayout = () => {
  const { RFQ, forms, setForms, addform, removeform, info, setInfo } = useContext(EstContext);

  const [quants, setQuants] = useState([]);
  const quantbox = useRef();

  const colorbar = 0.250;
  const gripper = 0.5;
  const edges = 0.250;
  const tail = 0.250;
  const tailpad = 0.0625;

// Add these lines after: useEffect(() => { setQuants... }, [info]);
const minQuant = quants.length > 0 
  ? Math.min(...quants.map(q => parseInt(q, 10))) 
  : null;

const maxQuant = quants.length > 0 
  ? Math.max(...quants.map(q => parseInt(q, 10))) 
  : null;

  const checkquans = (e) => {
    if (e.key === "Enter" && e.target.value.trim() !== "") {
      e.preventDefault();
      const newVal = e.target.value.trim();
      if (quants.includes(newVal)) return;

      const updated = [...quants, newVal].sort((a, b) => parseInt(a) - parseInt(b));
      setQuants(updated);
      quantbox.current.value = "";
    }
  };

  const removequant = (item) => {
    setQuants(quants.filter(q => parseInt(q) !== parseInt(item)));
  };

  const addpressoption = () => {
    if (!info?.options) return;
    const deepCopyObj = JSON.parse(JSON.stringify(info.options[0]));
    deepCopyObj.itemid = uuidv4();
    setInfo({ ...info, options: [...info.options, deepCopyObj] });
  };

  const removeoption = (id) => {
    const filtered = info?.options?.filter(opt => opt.itemid !== id) || [];
    setInfo({ ...info, options: filtered });
  };

  return (
  
    <div >
      <MainWrap >
        {/* Header Card */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl font-bold text-teal-700">Quote {info?.quote}</h1>
              <p className="text-gray-500 mt-1">
                <Moment format="MMMM DD, YYYY">{info?.enterdate}</Moment>
              </p>
            </div>

            <div className="flex gap-4">
              <button className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-2xl font-medium transition" title="Save Estimate">
                <Save size={24} /> Save
              </button>
              <button className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-2xl font-medium transition" title="Print">
                <List size={24} /> Print
              </button>
              <button className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-2xl font-medium transition" title="Copy Estimate">
                <BookCopy size={24} /> Copy
              </button>
            </div>
          </div>

          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <input 
                className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" 
                value={info?.customer || ''} 
                readOnly 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <input 
                className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" 
                value={info?.projectname || ''} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Style</label>
              <input 
                className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" 
                value={info?.projectstyle || ''} 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" value={info?.contact || ''} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Rep</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" value={info?.salesrep || ''} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CSR</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" value={info?.csr || ''} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Due Date</label>
              <div className="px-5 py-3 border border-gray-300 rounded-2xl bg-gray-50">
                <Moment format="MM/DD/YYYY">{info?.duedate}</Moment>
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prev Job#</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prev Quote #</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimator</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Whole Job As</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input className="w-full px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
        </div>

        {/* Quantities & Notes */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <label className="text-lg font-semibold text-gray-800">Quantities</label>
            <input 
              ref={quantbox}
              placeholder="Add Quantity Here (press Enter)" 
              onKeyDown={checkquans}
              className="flex-1 max-w-xs px-5 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            {quants.map((item, index) => (
              <div key={index} className="bg-teal-50 border border-teal-200 rounded-2xl px-6 py-3 flex items-center gap-3 group">
                <span className="font-medium text-teal-800">{item}</span>
                <X 
                  size={18} 
                  className="cursor-pointer text-red-500 hover:text-red-700 opacity-60 group-hover:opacity-100" 
                  onClick={() => removequant(item)} 
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Production Notes</label>
            <textarea 
              value={info?.pnotes?.replace(/&amp;/g, "&") || ''} 
              className="w-full h-32 px-5 py-4 border border-gray-300 rounded-3xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y"
            />
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
          <Tabs>
            <TabList className="flex border-b border-gray-200 bg-gray-50 px-8 pt-2">
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                Forms
              </Tab>
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                Components
              </Tab>
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                Bindery
              </Tab>
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                Mailing
              </Tab>
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                OSP
              </Tab>
              <Tab className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none">
                Cost Summary
              </Tab>
            </TabList>

            <TabPanel className="p-8">
              <Tabs>
                <TabList className="flex border-b border-gray-200 mb-6">
                  <span 
                    onClick={addpressoption}
                    className="flex items-center justify-center w-11 h-11 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-2xl cursor-pointer mr-4 transition"
                  >
                    <Plus size={28} strokeWidth={3} />
                  </span>

                  {info?.options?.map((item, index) => (
                    <Tab key={index} className="px-8 py-4 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:text-teal-700 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none flex items-center gap-2">
                      Option {index + 1}
                      {index > 0 && (
                        <X 
                          size={20} 
                          className="text-red-500 hover:text-red-700 cursor-pointer" 
                          onClick={(e) => { e.stopPropagation(); removeoption(item.itemid); }} 
                        />
                      )}
                    </Tab>
                  ))}
                </TabList>

                {info?.options?.map((item, index) => (
                  <TabPanel key={index}>
                    <FormSetup option={index} item={item} />
                  </TabPanel>
                ))}
              </Tabs>
            </TabPanel>

            {/* Other TabPanels can be added here later */}
          </Tabs>
        </div>
      </MainWrap>
    </div>
  );
};

const Estimates = () => <SetLayout />;

export default Estimates;


